"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const FlexContainer_1 = require("./FlexContainer");
const FlexItem_1 = require("./FlexItem");
/**
 * This is the connection between the render tree with the layout tree of this flex container/item.
 */
class FlexNode {
    constructor(subject) {
        this.subject = subject;
        /**
         * Possible values (only in case of container):
         * bit 0: has changed or contains items with changes
         * bit 1: width changed
         * bit 2: height changed
         */
        this._recalc = 0;
        this._enabled = false;
        this.x = 0;
        this.y = 0;
        this.w = 0;
        this.h = 0;
        // When a flex node is skipped, it does not take part in the flex layout.
        // Both ancestors and descendants are considered to be at this node's level instead.
        this._skip = false;
    }
    get flexLayout() {
        return this.isFlexEnabled() ? this.flex.layout : undefined;
    }
    layoutFlexTree() {
        if (this.isFlexEnabled()) {
            this.flexLayout.layoutTree();
        }
    }
    get flex() {
        this._ensureFlex();
        return this._flex;
    }
    _ensureFlex() {
        if (!this._flex) {
            this._flex = new FlexContainer_1.FlexContainer(this);
        }
    }
    setFlexEnabled(v) {
        // When skipped, defer enabling until no longer skipped.
        if (!this._skip) {
            if (v) {
                this.enableFlex();
            }
            else {
                this.disableFlex();
            }
        }
    }
    enableFlex() {
        this.forceLayout();
        this.enableChildrenAsFlexItems();
        this.updateEnabledFlag();
    }
    disableFlex() {
        this.forceLayout();
        this.disableChildrenAsFlexItems();
        this.updateEnabledFlag();
    }
    get skip() {
        return this._skip;
    }
    set skip(value) {
        if (this._skip !== value) {
            if (value) {
                this.setSkipped();
            }
            else {
                this.unsetSkipped();
            }
        }
    }
    setSkipped() {
        const flexParent = this.flexParent;
        const isFlexEnabled = this.isFlexEnabled();
        this._skip = true;
        if (isFlexEnabled) {
            // We disable flex to simplify some cache-related stuff.
            this.disableFlex();
        }
        if (flexParent) {
            this.disableFlexItem();
            this.enableChildrenAsFlexItems();
            flexParent.changedChildren();
        }
    }
    unsetSkipped() {
        var _a;
        this._skip = false;
        const parentIsFlex = (_a = this.getParent()) === null || _a === void 0 ? void 0 : _a.isFlexEnabled();
        if (parentIsFlex) {
            this.disableChildrenAsFlexItems();
            this.enableFlexItem();
            this.getParent().changedChildren();
        }
        if (this._flex && this._flex.enabled) {
            this.enableFlex();
        }
    }
    isFlexEnabled() {
        return !this._skip && (this._flex ? this._flex.enabled : false);
    }
    isFlexItemEnabled() {
        return !this._skip && this.flexParent !== undefined;
    }
    isEnabled() {
        return this.isFlexEnabled() || this.isFlexItemEnabled();
    }
    get flexItem() {
        this.ensureFlexItem();
        return this._flexItem;
    }
    ensureFlexItem() {
        if (!this._flexItem) {
            this._flexItem = new FlexItem_1.FlexItem(this);
        }
    }
    getChildren() {
        const results = [];
        const children = this.subject.getChildren();
        if (children) {
            const n = children.length;
            for (let i = 0; i < n; i++) {
                const child = children[i];
                FlexNode.gatherChildren(child, results);
            }
        }
        return results;
    }
    static gatherChildren(child, results) {
        if (child.hasLayout() && child.getLayout().skip) {
            const children = child.getChildren();
            if (children) {
                const n = children.length;
                for (let i = 0; i < n; i++) {
                    const child = children[i];
                    this.gatherChildren(child, results);
                }
            }
        }
        else {
            results.push(child.getLayout());
        }
    }
    enableChildrenAsFlexItems() {
        const children = this.getChildren();
        if (children) {
            for (let i = 0, n = children.length; i < n; i++) {
                const child = children[i];
                child.enableFlexItem();
            }
        }
    }
    disableChildrenAsFlexItems() {
        const children = this.getChildren();
        if (children) {
            for (let i = 0, n = children.length; i < n; i++) {
                const child = children[i];
                child.disableFlexItem();
            }
        }
    }
    enableFlexItem() {
        this.ensureFlexItem();
        const flexParent = this.getParent();
        this._flexItem.setContainer(flexParent._flex);
        flexParent.changedContents();
        this.updateEnabledFlag();
    }
    disableFlexItem() {
        if (this._flexItem) {
            this._flexItem.setContainer(undefined);
        }
        // We keep the flexItem object because it may contain custom settings.
        this.updateEnabledFlag();
    }
    updateEnabledFlag() {
        const enabled = this.isEnabled();
        if (this._enabled !== enabled) {
            if (!enabled) {
                this.disable();
            }
            this._enabled = enabled;
        }
    }
    disable() {
        this.restoreSubjectToNonFlex();
    }
    restoreSubjectToNonFlex() {
        const subject = this.subject;
        subject.setLayoutCoords(subject.getSourceX(), subject.getSourceY());
        subject.setLayoutDimensions(subject.getSourceW(), subject.getSourceH());
    }
    getParent() {
        const activeParent = FlexNode.getActiveLayoutSubject(this.subject.getParent());
        return activeParent ? activeParent.getLayout() : undefined;
    }
    setParent(from, to) {
        const fromNode = FlexNode.getActiveLayoutNode(from);
        if (fromNode === null || fromNode === void 0 ? void 0 : fromNode.isFlexEnabled()) {
            fromNode.changedChildren();
        }
        const toNode = FlexNode.getActiveLayoutNode(to);
        if (toNode === null || toNode === void 0 ? void 0 : toNode.isFlexEnabled()) {
            if (this._skip) {
                this.enableChildrenAsFlexItems();
            }
            else {
                this.enableFlexItem();
            }
            toNode.changedChildren();
        }
        else {
            if (fromNode === null || fromNode === void 0 ? void 0 : fromNode.isFlexEnabled()) {
                if (this._skip) {
                    this.disableChildrenAsFlexItems();
                }
                else {
                    this.disableFlexItem();
                }
                fromNode.changedChildren();
            }
        }
    }
    get flexParent() {
        if (!this.flexItem.enabled) {
            return undefined;
        }
        const parent = this.getParent();
        if (parent && parent.isFlexEnabled()) {
            return parent;
        }
        return undefined;
    }
    updateVisible() {
        const parent = this.flexParent;
        if (parent) {
            parent.changedChildren();
        }
    }
    get items() {
        if (!this._items) {
            this._items = this.getFlexItems();
        }
        return this._items;
    }
    getFlexItems() {
        const items = [];
        const children = this.getChildren();
        if (children) {
            for (let i = 0, n = children.length; i < n; i++) {
                const item = children[i];
                if (item.subject.isDisplayed()) {
                    if (item.isFlexItemEnabled()) {
                        items.push(item);
                    }
                }
            }
        }
        return items;
    }
    changedChildren() {
        this.clearFlexItemsCache();
        this.changedContents();
    }
    clearFlexItemsCache() {
        this._items = undefined;
    }
    setLayout(x, y, w, h) {
        const subject = this.subject;
        let sourceX = subject.getSourceX();
        let sourceY = subject.getSourceY();
        if (this.sourceFuncX) {
            sourceX = this.sourceFuncX(this.getParentAxisSizeWithPadding(true), this.getParentAxisSizeWithPadding(false));
        }
        if (this.sourceFuncY) {
            sourceY = this.sourceFuncY(this.getParentAxisSizeWithPadding(true), this.getParentAxisSizeWithPadding(false));
        }
        if (this.isFlexItemEnabled()) {
            subject.setLayoutCoords(x + sourceX, y + sourceY);
        }
        else {
            // Reuse the x,y 'settings'.
            subject.setLayoutCoords(sourceX, sourceY);
        }
        subject.setLayoutDimensions(w, h);
    }
    forceLayout(changeWidth = true, changeHeight = true) {
        this.updateRecalc(changeWidth, changeHeight);
    }
    changedContents() {
        this.updateRecalc();
    }
    isChanged() {
        return this._recalc > 0;
    }
    updateRecalc(changeExternalWidth = false, changeExternalHeight = false) {
        if (this.isFlexEnabled()) {
            const layout = this._flex.layout;
            // When something internal changes, it can have effect on the external dimensions.
            changeExternalWidth = changeExternalWidth || layout.isAxisFitToContents(true);
            changeExternalHeight = changeExternalHeight || layout.isAxisFitToContents(false);
        }
        const recalc = 1 + (changeExternalWidth ? 2 : 0) + (changeExternalHeight ? 4 : 0);
        const newRecalcFlags = this.getNewRecalcFlags(recalc);
        this._recalc |= recalc;
        if (newRecalcFlags > 1) {
            if (this.flexParent) {
                this.flexParent.updateRecalcBottomUp(recalc);
            }
            else {
                this.subject.triggerLayout();
            }
        }
        else {
            this.subject.triggerLayout();
        }
    }
    getNewRecalcFlags(flags) {
        return (7 - this._recalc) & flags;
    }
    updateRecalcBottomUp(childRecalc) {
        const newRecalc = this._getRecalcFromChangedChildRecalc(childRecalc);
        const newRecalcFlags = this.getNewRecalcFlags(newRecalc);
        this._recalc |= newRecalc;
        if (newRecalcFlags > 1) {
            const flexParent = this.flexParent;
            if (flexParent) {
                flexParent.updateRecalcBottomUp(newRecalc);
            }
            else {
                this.subject.triggerLayout();
            }
        }
        else {
            this.subject.triggerLayout();
        }
    }
    _getRecalcFromChangedChildRecalc(childRecalc) {
        const layout = this._flex.layout;
        const mainAxisRecalcFlag = layout.horizontal ? 1 : 2;
        const crossAxisRecalcFlag = layout.horizontal ? 2 : 1;
        const crossAxisDimensionsChangedInChild = childRecalc & crossAxisRecalcFlag;
        if (!crossAxisDimensionsChangedInChild) {
            const mainAxisDimensionsChangedInChild = childRecalc & mainAxisRecalcFlag;
            if (mainAxisDimensionsChangedInChild) {
                const mainAxisIsWrapping = layout.isWrapping();
                if (mainAxisIsWrapping) {
                    const crossAxisIsFitToContents = layout.isCrossAxisFitToContents();
                    if (crossAxisIsFitToContents) {
                        // Special case: due to wrapping, the cross axis size may be changed.
                        childRecalc += crossAxisRecalcFlag;
                    }
                }
            }
        }
        let isWidthDynamic = layout.isAxisFitToContents(true);
        let isHeightDynamic = layout.isAxisFitToContents(false);
        if (layout.hasShrunk()) {
            // If during previous layout this container was 'shrunk', any changes may change the 'min axis size' of the
            // contents, leading to a different axis size on this container even when it was not 'fit to contents'.
            if (layout.horizontal) {
                isWidthDynamic = true;
            }
            else {
                isHeightDynamic = true;
            }
        }
        const localRecalc = 1 + (isWidthDynamic ? 2 : 0) + (isHeightDynamic ? 4 : 0);
        return childRecalc & localRecalc;
    }
    get recalc() {
        return this._recalc;
    }
    clearRecalcFlag() {
        this._recalc = 0;
    }
    enableLocalRecalcFlag() {
        this._recalc = 1;
    }
    updatedSourceW() {
        this.forceLayout(true, false);
    }
    updatedSourceH() {
        this.forceLayout(false, true);
    }
    get sourceFuncX() {
        return this.subject.getSourceFuncX();
    }
    get sourceFuncY() {
        return this.subject.getSourceFuncY();
    }
    get sourceFuncW() {
        return this.subject.getSourceFuncW();
    }
    get sourceFuncH() {
        return this.subject.getSourceFuncH();
    }
    getAxisLayoutSize(horizontal) {
        return horizontal ? this.w : this.h;
    }
    setAxisLayoutSize(horizontal, size) {
        if (horizontal) {
            this.w = size;
        }
        else {
            this.h = size;
        }
    }
    getAxisLayoutPos(horizontal) {
        return horizontal ? this.x : this.y;
    }
    setAxisLayoutPos(horizontal, pos) {
        if (horizontal) {
            this.x = pos;
        }
        else {
            this.y = pos;
        }
    }
    getParentAxisSizeWithPadding(horizontal) {
        const flexParent = this.getParent();
        if (!flexParent) {
            return 0;
        }
        else {
            if (flexParent.isFlexEnabled()) {
                // Use pending layout size.
                return flexParent.getAxisLayoutSize(horizontal) + flexParent.getTotalPadding(horizontal);
            }
            else {
                // Use layouted size.
                const parentSubject = flexParent.subject;
                return horizontal ? parentSubject.getLayoutW() : parentSubject.getLayoutH();
            }
        }
    }
    getHorizontalPaddingOffset() {
        return this.getPaddingOffset(true);
    }
    getVerticalPaddingOffset() {
        return this.getPaddingOffset(false);
    }
    getPaddingOffset(horizontal) {
        if (this.isFlexEnabled()) {
            const flex = this.flex;
            if (horizontal) {
                return flex.paddingLeft;
            }
            else {
                return flex.paddingTop;
            }
        }
        else {
            return 0;
        }
    }
    getHorizontalPadding() {
        return this.getTotalPadding(true);
    }
    getVerticalPadding() {
        return this.getTotalPadding(false);
    }
    getTotalPadding(horizontal) {
        if (this.isFlexEnabled()) {
            const flex = this.flex;
            if (horizontal) {
                return flex.paddingRight + flex.paddingLeft;
            }
            else {
                return flex.paddingTop + flex.paddingBottom;
            }
        }
        else {
            return 0;
        }
    }
    getRelAxisSize(horizontal) {
        if (horizontal) {
            if (this.sourceFuncW) {
                if (this.allowRelAxisSizeFunction(true)) {
                    return this.sourceFuncW(this.getParentAxisSizeWithPadding(true), this.getParentAxisSizeWithPadding(false));
                }
                else {
                    return 0;
                }
            }
            else {
                return this.subject.getSourceW();
            }
        }
        else {
            if (this.sourceFuncH) {
                if (this.allowRelAxisSizeFunction(false)) {
                    return this.sourceFuncH(this.getParentAxisSizeWithPadding(true), this.getParentAxisSizeWithPadding(false));
                }
                else {
                    return 0;
                }
            }
            else {
                return this.subject.getSourceH();
            }
        }
    }
    getMarginOffset(horizontal) {
        const flexItem = this.flexItem;
        if (flexItem) {
            if (horizontal) {
                return flexItem.marginLeft;
            }
            else {
                return flexItem.marginTop;
            }
        }
        else {
            return 0;
        }
    }
    getHorizontalMarginOffset() {
        return this.getMarginOffset(true);
    }
    getVerticalMarginOffset() {
        return this.getMarginOffset(false);
    }
    getTotalMargin(horizontal) {
        const flexItem = this.flexItem;
        if (flexItem) {
            if (horizontal) {
                return flexItem.marginRight + flexItem.marginLeft;
            }
            else {
                return flexItem.marginTop + flexItem.marginBottom;
            }
        }
        else {
            return 0;
        }
    }
    allowRelAxisSizeFunction(horizontal) {
        const flexParent = this.flexParent;
        if (flexParent && flexParent.flex.layout.isAxisFitToContents(horizontal)) {
            // We don't allow relative width on fit-to-contents because it leads to conflicts.
            return false;
        }
        return true;
    }
    isZeroAxisSize(horizontal) {
        if (horizontal) {
            return !this.subject.getSourceW() && !this.sourceFuncW;
        }
        else {
            return !this.subject.getSourceH() && !this.sourceFuncH;
        }
    }
    getAxisMinSize(horizontal) {
        let minSize = this.getPlainAxisMinSize(horizontal);
        let flexItemMinSize = 0;
        if (this.isFlexItemEnabled()) {
            flexItemMinSize = this.flexItem.getMinSizeSetting(horizontal);
        }
        const hasLimitedMinSize = flexItemMinSize > 0;
        if (hasLimitedMinSize) {
            minSize = Math.max(minSize, flexItemMinSize);
        }
        return minSize;
    }
    getPlainAxisMinSize(horizontal) {
        if (this.isFlexEnabled()) {
            return this.flex.layout.getAxisMinLineSize(horizontal);
        }
        else {
            const isShrinkable = this.flexItem.shrink !== 0;
            if (isShrinkable) {
                return 0;
            }
            else {
                return this.getRelAxisSize(horizontal);
            }
        }
    }
    resizeAxis(horizontal, size) {
        if (this.isFlexEnabled()) {
            const flex = this.flex;
            const isMainAxis = flex.horizontal === horizontal;
            if (isMainAxis) {
                flex.layout.resizeMainAxis(size);
            }
            else {
                flex.layout.resizeCrossAxis(size);
            }
        }
        else {
            this.setAxisLayoutSize(horizontal, size);
        }
    }
    isLayoutRoot() {
        return this.isFlexEnabled() && !this.isFlexItemEnabled();
    }
    static getActiveLayoutNode(subject) {
        var _a;
        return (_a = this.getActiveLayoutSubject(subject)) === null || _a === void 0 ? void 0 : _a.getLayout();
    }
    static getActiveLayoutSubject(subject) {
        let current = subject;
        while (current && current.hasLayout() && current.getLayout().skip) {
            current = current.getParent();
        }
        return current;
    }
}
exports.FlexNode = FlexNode;
//# sourceMappingURL=FlexNode.js.map